import { RefObject, useCallback, useState } from "react";
import { flushSync } from "react-dom";
import { Company } from "../../../setups/types/setupTypes";
import { Employee } from "../../types/processTypes";
import {
  buildAndSavePdfFromDom,
  buildBulkCvFileName,
} from "../utils/buildCvPdf";

function waitForPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/**
 * Downloads one PDF per office by rendering each CV in the on-screen preview
 * (same path as single download). Offscreen capture breaks facePhoto layout.
 */
export function useBulkOfficePdfDownload(
  previewRootRef: RefObject<HTMLElement | null>,
) {
  const [pdfTarget, setPdfTarget] = useState<Company | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadBulk = useCallback(
    async (offices: Company[], employee: Employee) => {
      if (!employee.givenName || offices.length === 0) return;

      setIsDownloading(true);
      try {
        for (const office of offices) {
          flushSync(() => setPdfTarget(office));
          await waitForPaint();

          const numberOfPages = office.cvTemplate?.numberOfPages ?? 1;
          await buildAndSavePdfFromDom(
            buildBulkCvFileName(employee, office),
            numberOfPages,
            previewRootRef.current,
          );
        }
      } finally {
        flushSync(() => setPdfTarget(null));
        setIsDownloading(false);
      }
    },
    [previewRootRef],
  );

  return { pdfTarget, downloadBulk, isDownloading };
}
